import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Section } from "@/components/Section";
import { Icon } from "@/components/Icon";
import { TgEmoji } from "@/components/TgEmoji";
import { ActionButton } from "@/components/ActionButton";
import {
  useCreateReferralLink,
  useReferralLinks,
  useUpdateReferralLink,
} from "@/api/hooks";
import type { ReferralLink, ReferralLinkStats } from "@/api/types";
import { tg } from "@/lib/telegram";
import { showToast } from "@/lib/toast";
import { formatAmount } from "@/lib/utils";

/**
 * «Мої посилання» у розділі «Реферали» (owner 2026-10-25, «як у кабінеті
 * партнера Findly»). Бачить лише людина з прапорцем `referral_links`: вона
 * створює кілька посилань — кожне зі своїм імʼям — і роздає їх. Усі, хто
 * прийшов за будь-яким, — її реферали з тими самими нарахуваннями.
 *
 * Цифри посилання: прийшли (запустили бота) → зараховані (підписались на
 * канал, +2 ₴) → тест (1 ₴) → оплатили підписку; «заробіток» — скільки з цих
 * людей нараховано на реферальний баланс.
 *
 * На головному списку — ЛИШЕ «зараховано» по кожному посиланню (owner 2026-10-26, «як у Findly»): власник
 * посилань скринить цей список і платить людям за кожного зарахованого, їм не треба бачити тести, оплати й
 * заробіток. Усе детально — у вікні посилання (тап по рядку) і в «Разом».
 */
export function ReferralLinksSection() {
  const q = useReferralLinks(true);
  const [popup, setPopup] = useState<PopupState | null>(null);
  const update = useUpdateReferralLink();

  if (q.isLoading || !q.data?.enabled) return null;
  const d = q.data;
  const active = d.links.filter((l) => l.active);
  const archived = d.links.filter((l) => !l.active);
  const canMake = d.links.length < d.max;

  const restore = async (l: ReferralLink) => {
    try {
      await update.mutateAsync({ id: l.id, archived: false });
      showToast("Посилання знову працює", { emoji: "✓" });
    } catch (e) {
      showToast((e as Error).message);
    }
  };

  return (
    <>
      <Section
        title={`Мої посилання · ${d.links.length} з ${d.max}`}
        bodyClassName="p-0"
        footer={
          d.links.length === 0 ? undefined : (
            <span className="block [text-wrap:balance]">
              Усі, хто прийде за будь-яким із цих посилань, — твої реферали з тими самими нарахуваннями.
            </span>
          )
        }
      >
        {d.links.length === 0 ? (
          <div className="p-4">
            <div className="font-display text-[0.875rem] font-semibold text-ink">
              Окреме посилання для кожного
            </div>
            <div className="mt-1 text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
              Створи посилання для кожного, хто запрошує людей, — і бач, скільки приводить кожен.
            </div>
          </div>
        ) : (
          <>
            {active.map((l) => (
              <LinkRow key={l.id} l={l} onOpen={() => setPopup({ mode: "details", link: l })} />
            ))}
            {active.length > 0 && (
              <TotalRow
                stats={d.total}
                count={active.length}
                archived={archived.length}
                onOpen={() => setPopup({ mode: "total", stats: d.total, count: active.length, archived: archived.length })}
              />
            )}
            {archived.map((l) => (
              <LinkRow key={l.id} l={l} onOpen={() => setPopup({ mode: "details", link: l })} />
            ))}
          </>
        )}
        <div className="border-t border-line p-3">
          <ActionButton disabled={!canMake} onClick={() => setPopup({ mode: "new" })}>
            <Icon name="plus" size={16} />
            {canMake ? "Нове посилання" : `Максимум ${d.max} посилань`}
          </ActionButton>
        </div>
      </Section>
      {popup && (
        <ReferralLinkPopup
          state={popup}
          onState={setPopup}
          onClose={() => setPopup(null)}
          onRestore={restore}
        />
      )}
    </>
  );
}

function dayLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return many;
  if (m10 === 1) return one;
  if (m10 >= 2 && m10 <= 4) return few;
  return many;
}

function money(v: number): string {
  return `${v < 0 ? "−" : ""}${formatAmount(Math.abs(v))} ₴`;
}

function Stats({ st }: { st: ReferralLinkStats }) {
  const cols: [number, string][] = [
    [st.came, "прийшли"],
    [st.credited, "зараховані"],
    [st.trials, "тест"],
    [st.payers, "оплатили"],
  ];
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {cols.map(([n, label]) => (
        <div key={label} className="rounded-xl bg-surface px-1 py-2 text-center">
          <div className="font-display text-[1rem] font-bold tabular-nums leading-none text-ink">
            {formatAmount(n)}
          </div>
          <div className="mt-1 text-[0.625rem] leading-none text-ink-dim">{label}</div>
        </div>
      ))}
    </div>
  );
}

function copyLink(link: string) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(link);
    tg()?.HapticFeedback?.notificationOccurred?.("success");
    showToast("Посилання скопійовано", { emoji: "✓" });
  }
}

function shareLink(link: string) {
  const url = `https://t.me/share/url?url=${encodeURIComponent(link)}`;
  const webapp = tg();
  if (webapp?.openTelegramLink) webapp.openTelegramLink(url);
  else window.open(url, "_blank");
}

/** Рядок посилання на головному списку: імʼя і скільки зараховано — і все (owner 2026-10-26). */
function LinkRow({ l, onOpen }: { l: ReferralLink; onOpen: () => void }) {
  const off = !l.active;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`flex w-full items-center gap-3 border-b border-line p-4 text-left active:bg-ink/[0.03] ${off ? "opacity-60" : ""}`}
    >
      <span
        className={`grid h-10 w-10 shrink-0 place-items-center rounded-[12px] ${
          off ? "bg-ink/[0.04] text-ink-faint" : "bg-primary/[0.08] text-primary"
        }`}
      >
        <Icon name="link" size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-[0.9375rem] font-bold text-ink">{l.name}</span>
        <span className="block text-[0.6875rem] text-ink-dim">
          rl{l.code} · {off ? `в архіві з ${dayLabel(l.archived_at)}` : `з ${dayLabel(l.created_at)}`}
        </span>
      </span>
      <Credited n={l.stats.credited} />
      <Icon name="arrow-right" size={14} color="#A8A29C" className="shrink-0" />
    </button>
  );
}

function Credited({ n }: { n: number }) {
  return (
    <span className="shrink-0 text-right">
      <span className="block whitespace-nowrap font-display text-[1.125rem] font-bold tabular-nums leading-none text-primary">
        {formatAmount(n)}
      </span>
      <span className="mt-1 block text-[0.625rem] leading-none text-ink-dim">зараховано</span>
    </span>
  );
}

function TotalRow({
  stats,
  count,
  archived,
  onOpen,
}: {
  stats: ReferralLinkStats;
  count: number;
  archived: number;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 border-b border-line bg-primary/[0.04] p-4 text-left active:bg-primary/[0.07]"
    >
      <span className="min-w-0 flex-1">
        <span className="block font-display text-[0.9375rem] font-bold text-ink [text-wrap:balance]">Разом по моїх посиланнях</span>
        <span className="block text-[0.6875rem] text-ink-dim">
          {count} {plural(count, "активне", "активні", "активних")}
          {archived > 0 ? ` · ${archived} в архіві` : ""}
        </span>
      </span>
      <Credited n={stats.credited} />
      <Icon name="arrow-right" size={14} color="#A8A29C" className="shrink-0" />
    </button>
  );
}

/** Уся статистика — лише у вікні посилання чи «Разом»: прийшли, зараховані, тест, оплатили, заробіток. */
function Details({ st }: { st: ReferralLinkStats }) {
  return (
    <div className="mt-4 rounded-2xl bg-bg/60 p-3">
      <Stats st={st} />
      <div className="mt-3 flex items-center justify-between border-t border-line px-1 pt-3">
        <span className="text-[0.8125rem] text-ink-dim">Заробіток</span>
        <span className="font-display text-[1rem] font-bold tabular-nums text-primary">{money(st.earned)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Попапи: нове посилання → готове; змінити → архів
// ---------------------------------------------------------------------------

export type PopupState =
  | { mode: "details"; link: ReferralLink }
  | { mode: "total"; stats: ReferralLinkStats; count: number; archived: number }
  | { mode: "new" }
  | { mode: "made"; link: ReferralLink }
  | { mode: "edit"; link: ReferralLink }
  | { mode: "archive"; link: ReferralLink };

export function ReferralLinkPopup({
  state,
  onState,
  onClose,
  onRestore,
  demo,
}: {
  state: PopupState;
  onState: (s: PopupState) => void;
  onClose: () => void;
  onRestore?: (l: ReferralLink) => Promise<void>;
  /** Галерея попапів: нічого не шле на сервер. */
  demo?: boolean;
}) {
  const create = useCreateReferralLink();
  const update = useUpdateReferralLink();
  const [name, setName] = useState(state.mode === "edit" ? state.link.name : "");
  const [err, setErr] = useState<string | null>(null);
  const busy = create.isPending || update.isPending;

  const fake = (nm: string): ReferralLink => ({
    id: 0,
    code: "48213907",
    name: nm,
    link: "https://t.me/inflow_chatbot?start=rl48213907",
    created_at: new Date().toISOString(),
    archived_at: null,
    active: true,
    stats: { came: 0, credited: 0, trials: 0, payers: 0, earned: 0 },
  });

  const doCreate = async () => {
    const nm = name.trim();
    if (!nm) {
      setErr("Вкажи, для кого це посилання.");
      return;
    }
    if (busy) return;
    setErr(null);
    try {
      const made = demo ? fake(nm) : await create.mutateAsync(nm);
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      onState({ mode: "made", link: made });
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const doSave = async (link: ReferralLink) => {
    const nm = name.trim();
    if (!nm) {
      setErr("Вкажи, для кого це посилання.");
      return;
    }
    if (nm === link.name) {
      onClose();
      return;
    }
    if (busy) return;
    try {
      if (!demo) await update.mutateAsync({ id: link.id, name: nm });
      showToast("Збережено", { emoji: "✓" });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const doArchive = async (link: ReferralLink) => {
    if (busy) return;
    try {
      if (!demo) await update.mutateAsync({ id: link.id, archived: true });
      showToast("Посилання в архіві", { emoji: "✓" });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const input = (onEnter: () => void) => (
    <div className="mt-4">
      <input
        type="text"
        maxLength={40}
        autoFocus
        placeholder="Наприклад, Аня"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setErr(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") onEnter();
        }}
        className={
          "w-full rounded-xl bg-bg/60 px-3 py-3 font-display text-[1rem] font-semibold text-ink outline-none ring-1 " +
          "placeholder:font-medium placeholder:text-ink-faint transition " +
          (err ? "ring-red-400" : "ring-ink/10 focus:ring-primary/40")
        }
      />
      {err && <div className="mt-1.5 text-[0.6875rem] leading-snug text-red-500 [text-wrap:balance]">{err}</div>}
    </div>
  );

  const head = (emoji: string, title: string, text: ReactNode) => (
    <div className="text-center">
      <div className="mb-2 flex justify-center">
        <TgEmoji char={emoji} size={48} />
      </div>
      <div className="mb-1.5 font-display text-[1.1875rem] font-bold leading-tight text-ink [text-wrap:balance]">
        {title}
      </div>
      <div className="text-[0.8125rem] leading-relaxed text-ink-dim [text-wrap:balance]">{text}</div>
    </div>
  );

  const cancel = (label = "Скасувати") => (
    <button
      type="button"
      disabled={busy}
      onClick={onClose}
      className="mt-2 w-full py-2.5 font-display text-[0.875rem] font-semibold text-ink-dim active:text-ink"
    >
      {label}
    </button>
  );

  let body: ReactNode = null;
  if (state.mode === "details") {
    const l = state.link;
    body = (
      <>
        {head(
          "🤝",
          l.name,
          <>
            {l.active ? `Посилання з ${dayLabel(l.created_at)}` : `В архіві з ${dayLabel(l.archived_at)} — нових не зараховує`}
          </>,
        )}
        <div className="mt-3 break-all rounded-xl bg-bg/60 px-3 py-2.5 text-center font-mono text-[0.75rem] text-ink">
          {l.link}
        </div>
        <Details st={l.stats} />
        {l.active ? (
          <>
            <ActionButton className="mt-4" onClick={() => copyLink(l.link)}>
              <Icon name="doc" size={16} />
              Скопіювати
            </ActionButton>
            <ActionButton className="mt-2" tone="secondary" onClick={() => shareLink(l.link)}>
              <Icon name="telegram" size={16} />
              Поділитися
            </ActionButton>
            <ActionButton
              className="mt-2"
              tone="secondary"
              onClick={() => {
                setName(l.name);
                onState({ mode: "edit", link: l });
              }}
            >
              <Icon name="edit" size={16} />
              Змінити
            </ActionButton>
          </>
        ) : (
          onRestore && (
            <ActionButton
              className="mt-4"
              disabled={busy}
              onClick={async () => {
                await onRestore(l);
                onClose();
              }}
            >
              Повернути з архіву
            </ActionButton>
          )
        )}
        {cancel("Закрити")}
      </>
    );
  } else if (state.mode === "total") {
    body = (
      <>
        {head(
          "💰",
          "Разом по моїх посиланнях",
          <>
            {state.count} {plural(state.count, "активне", "активні", "активних")}
            {state.archived > 0 ? ` · ${state.archived} в архіві` : ""}
          </>,
        )}
        <Details st={state.stats} />
        {cancel("Закрити")}
      </>
    );
  } else if (state.mode === "new") {
    body = (
      <>
        {head("🤝", "Нове посилання", "Для кого це посилання? Імʼя бачиш лише ти.")}
        {input(doCreate)}
        <ActionButton className="mt-4" disabled={busy || !name.trim()} onClick={doCreate}>
          {busy ? "Створюю…" : "Створити"}
        </ActionButton>
        {cancel()}
      </>
    );
  } else if (state.mode === "made") {
    const l = state.link;
    body = (
      <>
        {head(
          "✅",
          "Посилання готове",
          <>
            «{l.name}» — надішли його, і всі, хто прийде за ним, стануть твоїми рефералами.
          </>,
        )}
        <div className="mt-4 break-all rounded-xl bg-bg/60 px-3 py-2.5 text-center font-mono text-[0.75rem] text-ink">
          {l.link}
        </div>
        <ActionButton className="mt-4" onClick={() => copyLink(l.link)}>
          <Icon name="doc" size={16} />
          Скопіювати
        </ActionButton>
        <ActionButton className="mt-2" tone="secondary" onClick={() => shareLink(l.link)}>
          <Icon name="telegram" size={16} />
          Поділитися
        </ActionButton>
        {cancel("Готово")}
      </>
    );
  } else if (state.mode === "edit") {
    const l = state.link;
    body = (
      <>
        {head("📝", "Змінити посилання", <span className="break-all font-mono text-[0.75rem]">{l.link}</span>)}
        {input(() => doSave(l))}
        <ActionButton className="mt-4" disabled={busy || !name.trim()} onClick={() => doSave(l)}>
          {busy ? "Зберігаю…" : "Зберегти"}
        </ActionButton>
        <ActionButton className="mt-2" tone="secondary" disabled={busy} onClick={() => onState({ mode: "archive", link: l })}>
          Перенести в архів
        </ActionButton>
        {cancel()}
      </>
    );
  } else {
    const l = state.link;
    body = (
      <>
        {head(
          "🔒",
          "Перенести в архів?",
          <>
            Посилання «{l.name}» перестане зараховувати нових людей. Ті, хто вже прийшов за ним, лишаються твоїми.
          </>,
        )}
        {err && <div className="mt-3 text-center text-[0.6875rem] text-red-500">{err}</div>}
        <ActionButton className="mt-5" disabled={busy} onClick={() => doArchive(l)}>
          {busy ? "Переношу…" : "В архів"}
        </ActionButton>
        {cancel()}
      </>
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="fade-in relative w-full max-w-sm rounded-3xl bg-surface p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {body}
      </div>
    </div>,
    document.body,
  );
}
